import React, { useState, useEffect } from 'react';
import { Clock, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { qrAPI, adminQRAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const QRSettings = () => {
  const [qrDuration, setQrDuration] = useState('15'); // Default a 15 minutos
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await qrAPI.getSettings();
      // El backend retorna la duración en MINUTOS
      setQrDuration(String(response.data.duration));
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // IMPORTANTE: El valor se envía en MINUTOS (no segundos)
      await adminQRAPI.updateSettings(parseInt(qrDuration));
      toast({
        title: "Configuración guardada",
        description: `Los QR ahora durarán ${qrDuration} ${qrDuration === '1' ? 'minuto' : 'minutos'}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo guardar la configuración",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-white">Cargando...</p>
      </div>
    );
  }

  return (
    <>
      <div className="p-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Configuración de QR</h2>
            <p className="text-[#A2A2A2]">Ajusta la duración del código QR dinámico</p>
          </div>

          {/* Settings Card */}
          <div className="bg-black border border-white/10 rounded-3xl p-8 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Duración del QR dinámico</h3>
                  <p className="text-[#A2A2A2] mb-4">
                    Define cuántos minutos durará el código QR antes de expirar
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Duración en minutos</Label>
                  <Select value={qrDuration} onValueChange={setQrDuration}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-2xl h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-white/20 text-white">
                      <SelectItem value="1">1 minuto</SelectItem>
                      <SelectItem value="2">2 minutos</SelectItem>
                      <SelectItem value="5">5 minutos</SelectItem>
                      <SelectItem value="10">10 minutos</SelectItem>
                      <SelectItem value="15">15 minutos (recomendado)</SelectItem>
                      <SelectItem value="30">30 minutos</SelectItem>
                      <SelectItem value="60">60 minutos (1 hora)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                  <p className="text-[#A2A2A2] text-sm">
                    <strong className="text-white">Nota:</strong> Duraciones más cortas ofrecen mayor seguridad, 
                    pero pueden ser menos convenientes para los usuarios. 15 minutos es el balance ideal.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            className="bg-white text-black hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 active:scale-95"
          >
            <Save className="w-5 h-5 mr-2" />
            Guardar configuración
          </Button>

          {/* Info Section */}
          <div className="border border-white/10 rounded-3xl p-6 space-y-3">
            <h4 className="text-lg font-semibold text-white">¿Por qué QR dinámico?</h4>
            <ul className="space-y-2 text-[#A2A2A2]">
              <li className="flex gap-2">
                <span>•</span>
                <span>Evita el fraude por screenshots o códigos compartidos</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>Cada código es único y expira automáticamente</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>Aumenta la seguridad de las transacciones</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>Protege los beneficios exclusivos de los miembros</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Toaster />
    </>
  );
};

export default QRSettings;
